/**
 * Leitura do painel — tudo o que o painel *decide*, fora do React.
 *
 * O componente React é difícil de testar sem navegador; esta camada não é. Então
 * ela fica com o que tem risco de estar errado — ler a linha do Cube pelo nome
 * completo do membro, formatar em pt-BR, escolher o tom de um indicador, montar
 * a opção do gráfico — e o componente fica só com marcação. Quando o painel
 * mostrar um número errado, o teste que falha é este, e ele roda com `node`.
 *
 * Uma regra que atravessa o arquivo: **o painel não calcula métrica**. Nada aqui
 * soma, divide ou deriva percentual. Faturamento, margem, giro, cobertura e
 * ruptura chegam prontos da semântica (ADR-05) e aqui só viram texto. Se um dia
 * aparecer uma conta neste arquivo, é sinal de que faltou uma medida no modelo —
 * e o lugar de resolver é `data/semantic`, não aqui.
 *
 * JavaScript puro de propósito, como `catalogo.js` no BFF: roda com
 * `node src/leitura.test.js`, sem npm install e sem navegador.
 */

const V = 'desempenho_comercial';
const E = 'saude_do_estoque';
const P = 'posicao_de_estoque';

/** Lê um membro da primeira linha de um bloco. `null` quando não veio. */
export function ler(linhas, membro) {
  if (!Array.isArray(linhas) || linhas.length === 0) return null;
  const v = linhas[0][membro];
  return v === undefined || v === null ? null : v;
}

// ----------------------------------------------------------------- formatos

const NUM = new Intl.NumberFormat('pt-BR');

/** Inteiro com separador de milhar. `—` quando não há dado. */
export function inteiro(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return NUM.format(Math.round(Number(v)));
}

export function decimal(v, casas = 1) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('pt-BR', {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  });
}

export function pct(v, casas = 1) {
  const t = decimal(v, casas);
  return t === '—' ? t : `${t}%`;
}

/**
 * Moeda abreviada, para cartão de indicador.
 *
 * `R$ 28,3 mi` em vez de `R$ 28.310.916,54`. Um cartão de KPI é lido de relance
 * e de longe, numa TV de sala de reunião; dezesseis dígitos ali não informam
 * mais, informam menos. O valor exato continua existindo — está na tabela, onde
 * a pessoa foi conferir de propósito.
 */
export function moeda(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const s = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e9) return `${s}R$ ${decimal(a / 1e9, 1)} bi`;
  if (a >= 1e6) return `${s}R$ ${decimal(a / 1e6, 1)} mi`;
  if (a >= 1e3) return `${s}R$ ${decimal(a / 1e3, 0)} mil`;
  return `${s}R$ ${decimal(a, 2)}`;
}

/** Moeda por extenso, para tabela — onde o usuário foi ver o valor exato. */
export function moedaCheia(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** `2026-07-01T00:00:00.000` → `jul/26`. */
export function rotuloMes(iso) {
  const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const [ano, mes] = String(iso).slice(0, 7).split('-').map(Number);
  if (!ano || !mes || mes < 1 || mes > 12) return String(iso).slice(0, 7);
  return `${MESES[mes - 1]}/${String(ano).slice(2)}`;
}

/** `2026-07-20` → `20/07/2026`. Sem `new Date`: fuso não entra onde não precisa. */
export function dataBr(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso);
}

// -------------------------------------------------------------- indicadores

/**
 * Faixas de cor dos cartões.
 *
 * São heurísticas de **exibição**, não regra de negócio: servem para o olho
 * achar o problema na tela, e por isso moram aqui e não na semântica. A política
 * de verdade — qual ruptura é aceitável para qual curva ABC — é decisão do
 * cliente e entra depois, versionada, junto das regras do Decision Center. Até
 * lá, valores de distribuidor, e o painel diz que são referências.
 */
const FAIXAS = {
  ruptura_pct: { bom: 3, ruim: 8, sentido: 'menor' },
  margem_bruta_pct: { bom: 22, ruim: 16, sentido: 'maior' },
  giro_anualizado: { bom: 6, ruim: 3, sentido: 'maior' },
  cobertura_media_dias: { bom: 45, ruim: 90, sentido: 'menor' },
  cobertura_de_custo_pct: { bom: 99, ruim: 90, sentido: 'maior' },
};

/** `bom` | `atencao` | `ruim` | `neutro`. */
export function tom(chave, valor) {
  const f = FAIXAS[chave];
  if (!f || valor === null || valor === undefined) return 'neutro';
  const v = Number(valor);
  if (f.sentido === 'menor') {
    if (v <= f.bom) return 'bom';
    return v >= f.ruim ? 'ruim' : 'atencao';
  }
  if (v >= f.bom) return 'bom';
  return v <= f.ruim ? 'ruim' : 'atencao';
}

/**
 * Os cartões do topo: as cinco métricas governadas, na ordem em que se lê uma
 * operação — quanto vendeu, com que margem, quanto de capital parado gira, por
 * quantos dias, e o que está faltando agora.
 *
 * A margem carrega a cobertura de custo no rodapé do cartão em vez de escondê-la
 * numa aba de qualidade de dado. Margem calculada sobre 94% dos itens é margem
 * superestimada, e quem decide precisa ver as duas coisas ao mesmo tempo.
 */
export function indicadores(blocos) {
  const c = blocos?.comercial_periodo;
  const e = blocos?.estoque_periodo;
  const p = blocos?.posicao_hoje;

  const cobertura = ler(c, `${V}.cobertura_de_custo_pct`);
  const notas = ler(c, `${V}.notas`);

  return [
    {
      chave: 'faturamento',
      rotulo: 'Faturamento',
      valor: moeda(ler(c, `${V}.faturamento`)),
      nota: notas === null ? null : `${inteiro(notas)} notas · ticket ${moeda(ler(c, `${V}.ticket_medio`))}`,
      tom: 'neutro',
    },
    {
      chave: 'margem_bruta_pct',
      rotulo: 'Margem bruta',
      valor: pct(ler(c, `${V}.margem_bruta_pct`)),
      nota: cobertura === null
        ? moeda(ler(c, `${V}.margem_bruta`))
        : `${moeda(ler(c, `${V}.margem_bruta`))} · custo em ${pct(cobertura)} dos itens`,
      tom: tom('margem_bruta_pct', ler(c, `${V}.margem_bruta_pct`)),
      alerta: cobertura !== null && Number(cobertura) < 98,
    },
    {
      chave: 'giro_anualizado',
      rotulo: 'Giro do estoque',
      valor: `${decimal(ler(e, `${E}.giro_anualizado`), 1)}×`,
      nota: `estoque médio ${moeda(ler(e, `${E}.valor_estoque_medio`))}`,
      tom: tom('giro_anualizado', ler(e, `${E}.giro_anualizado`)),
    },
    {
      chave: 'cobertura_media_dias',
      rotulo: 'Cobertura média',
      valor: `${decimal(ler(e, `${E}.cobertura_media_dias`), 0)} dias`,
      nota: 'quanto tempo o estoque atual aguenta o consumo',
      tom: tom('cobertura_media_dias', ler(e, `${E}.cobertura_media_dias`)),
    },
    {
      chave: 'ruptura_pct',
      rotulo: 'Ruptura hoje',
      valor: pct(ler(p, `${P}.ruptura_pct`)),
      nota: `${inteiro(ler(p, `${P}.itens_em_ruptura`))} de ${inteiro(ler(p, `${P}.itens`))} itens`,
      tom: tom('ruptura_pct', ler(p, `${P}.ruptura_pct`)),
    },
  ];
}

/**
 * Capital parado, que fica ao lado dos cartões e não dentro deles.
 *
 * É a contrapartida da ruptura: quem persegue ruptura zero compra demais, e o
 * custo aparece aqui. As duas juntas evitam o painel que premia um erro.
 */
export function capitalParado(blocos) {
  const p = blocos?.posicao_hoje;
  const valor = ler(p, `${P}.valor_sem_consumo`);
  const total = ler(p, `${P}.valor_estoque`);
  return {
    valor: moeda(valor),
    itens: inteiro(ler(p, `${P}.itens_sem_consumo`)),
    participacao: valor === null || !total ? null : pct((100 * Number(valor)) / Number(total)),
  };
}

// ----------------------------------------------------------------- gráficos

/**
 * Faturamento em barra, margem em linha, dois eixos.
 *
 * O eixo da margem não começa em zero: a variação relevante de margem de
 * distribuidor cabe em três pontos percentuais, e forçar o zero achata a linha
 * até ela virar uma reta que não informa nada. O eixo do faturamento **começa**
 * em zero, porque ali a comparação é de tamanho e cortar a base exagera a
 * diferença entre as barras.
 */
export function opcaoMensal(linhas) {
  const dados = Array.isArray(linhas) ? linhas : [];
  const rotulos = dados.map((l) => rotuloMes(l[`${V}.data.month`] ?? l[`${V}.data`]));
  const faturamento = dados.map((l) => Number(l[`${V}.faturamento`] ?? 0));
  const margem = dados.map((l) => Number(l[`${V}.margem_bruta_pct`] ?? 0));

  return {
    grid: { left: 8, right: 8, top: 32, bottom: 8, containLabel: true },
    tooltip: { trigger: 'axis' },
    legend: { data: ['Faturamento', 'Margem %'], top: 0, icon: 'roundRect' },
    xAxis: { type: 'category', data: rotulos, axisTick: { show: false } },
    yAxis: [
      { type: 'value', min: 0, axisLabel: { formatter: (v) => moeda(v) }, splitLine: { show: true } },
      { type: 'value', scale: true, axisLabel: { formatter: '{value}%' }, splitLine: { show: false } },
    ],
    series: [
      {
        name: 'Faturamento', type: 'bar', data: faturamento, yAxisIndex: 0,
        itemStyle: { color: '#2563eb', borderRadius: [3, 3, 0, 0] },
      },
      {
        name: 'Margem %', type: 'line', data: margem, yAxisIndex: 1, smooth: true,
        symbolSize: 6, lineStyle: { width: 2 }, itemStyle: { color: '#f59e0b' },
      },
    ],
  };
}

/**
 * Ranking por filial, barra horizontal.
 *
 * O ECharts desenha o eixo de categoria de baixo para cima, então a lista chega
 * do BFF em ordem decrescente e é invertida aqui — sem isso, o maior aparece no
 * rodapé do gráfico e o ranking se lê de trás para frente.
 */
export function opcaoFilial(linhas) {
  const dados = [...(Array.isArray(linhas) ? linhas : [])].reverse();
  return {
    grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    xAxis: { type: 'value', axisLabel: { formatter: (v) => moeda(v) } },
    yAxis: { type: 'category', data: dados.map((l) => l[`${V}.filial_nome`]), axisTick: { show: false } },
    series: [{
      type: 'bar',
      data: dados.map((l) => Number(l[`${V}.faturamento`] ?? 0)),
      itemStyle: { color: '#2563eb', borderRadius: [0, 3, 3, 0] },
      label: { show: true, position: 'right', formatter: (p) => moeda(p.value) },
    }],
  };
}

// ------------------------------------------------------------------ tabelas

/**
 * A lista do que falta, já formatada.
 *
 * `urgencia` é o que ordena visualmente: ruptura primeiro, depois cobertura
 * crítica. A ordem das linhas em si vem do BFF (por consumo diário) e não é
 * refeita aqui — reordenar no cliente seria inventar um critério que o servidor
 * não conhece, e o dia em que a lista for paginada os dois discordariam.
 */
export function tabelaCriticos(linhas) {
  return (Array.isArray(linhas) ? linhas : []).map((l) => ({
    codigo: l[`${P}.produto_codigo`],
    descricao: l[`${P}.produto_descricao`],
    filial: l[`${P}.filial_nome`],
    faixa: l[`${P}.faixa_de_cobertura`],
    urgencia: l[`${P}.faixa_de_cobertura`] === 'ruptura' ? 'ruim' : 'atencao',
    saldo: inteiro(l[`${P}.saldo`]),
    consumo: decimal(l[`${P}.consumo_diario`], 1),
    cobertura: l[`${P}.faixa_de_cobertura`] === 'ruptura'
      ? 'zerado' : `${decimal(l[`${P}.cobertura_dias`], 0)} dias`,
  }));
}

/** A lista do capital parado. Mesmo formato, outra dor. */
export function tabelaExcesso(linhas) {
  return (Array.isArray(linhas) ? linhas : []).map((l) => ({
    codigo: l[`${P}.produto_codigo`],
    descricao: l[`${P}.produto_descricao`],
    filial: l[`${P}.filial_nome`],
    saldo: inteiro(l[`${P}.saldo`]),
    valor: moedaCheia(l[`${P}.valor_estoque`]),
  }));
}

/** Ranking por família, para a lista ao lado do gráfico. */
export function tabelaFamilias(linhas) {
  return (Array.isArray(linhas) ? linhas : []).map((l) => ({
    familia: l[`${V}.produto_familia`],
    faturamento: moeda(l[`${V}.faturamento`]),
    margem: pct(l[`${V}.margem_bruta_pct`]),
    tom: tom('margem_bruta_pct', l[`${V}.margem_bruta_pct`]),
  }));
}
